import * as THREE from "three";
import { buildKowry, kowryRendererSettings } from "../../packages/fractalar-kowry-renderer/src/index";
import {
  catalogIndexFromUrl,
  connectCatalogNavigation,
  updateCatalogUrl,
} from "./catalogNavigation";
import { createEnvironmentController } from "./environmentController";
import { DEFAULT_ENVIRONMENT } from "./environmentPresets";
import { connectEnvironmentPanel } from "./environmentPanel";
import "./genesis002AssetCatalog";
import { genesis002Catalog } from "./genesis002Catalog";
import { createObservationCamera } from "./observationCamera";
import { createObservationEnvironment } from "./observationEnvironment";
import { addObservationLighting } from "./observationLighting";
import { createWorldResponse, WORLD_VECTOR } from "./observationWorld";
import "./viewer.css";

const canvas = requireElement<HTMLCanvasElement>("kowry-canvas");
const status = requireElement<HTMLElement>("viewer-status");
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: "high-performance" });
renderer.outputColorSpace = kowryRendererSettings.outputColorSpace;
renderer.toneMapping = kowryRendererSettings.toneMapping;
renderer.toneMappingExposure = kowryRendererSettings.toneMappingExposure;
renderer.setPixelRatio(Math.min(window.devicePixelRatio, kowryRendererSettings.pixelRatioCap));

const scene = new THREE.Scene();
scene.background = new THREE.Color(DEFAULT_ENVIRONMENT.scene.background);
scene.fog = new THREE.FogExp2(DEFAULT_ENVIRONMENT.scene.fog, DEFAULT_ENVIRONMENT.scene.fogDensity);

const { camera, controls, frameObject, resize } = createObservationCamera(canvas, renderer);
const lighting = addObservationLighting(scene, WORLD_VECTOR);

const presentation = new THREE.Group();
presentation.name = "kowry-presentation";
scene.add(presentation);

let updateWorld: (deltaSeconds: number) => void = () => undefined;
let updateEnvironment: (deltaSeconds: number) => void = () => undefined;
let updateAppearance: (deltaSeconds: number) => void = lighting.update;
let currentRoot: THREE.Object3D | null = null;
let worldInitialized = false;
let selectionGeneration = 0;

const navigation = connectCatalogNavigation(genesis002Catalog.items, (index) => {
  void displayCatalogItem(index, "push");
});

requestAnimationFrame(() => {
  void displayCatalogItem(catalogIndexFromUrl(genesis002Catalog.items), "replace");
});

window.addEventListener("popstate", () => {
  void displayCatalogItem(catalogIndexFromUrl(genesis002Catalog.items), "none");
});

async function displayCatalogItem(
  index: number,
  historyMode: "push" | "replace" | "none",
): Promise<void> {
  const item = genesis002Catalog.items[index];
  if (!item) return;
  const generation = ++selectionGeneration;
  navigation.setLoading(true);
  status.removeAttribute("data-state");
  status.textContent = `Reconstructing ${item.name}…`;
  await nextFrame();
  if (generation !== selectionGeneration) return;

  if (currentRoot) {
    presentation.remove(currentRoot);
    disposeObject(currentRoot);
    renderer.renderLists.dispose();
    currentRoot = null;
  }

  let result: ReturnType<typeof renderKowry>;
  try {
    result = renderKowry(genesis002Catalog.collectionId, item.itemSeed);
  } catch (error) {
    console.error(error);
    status.textContent = `${item.name} could not be reconstructed.`;
    status.dataset.state = "error";
    navigation.setLoading(false);
    return;
  }

  try {
    const bounds = new THREE.Box3().setFromObject(result.root);
    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const radius = Math.max(size.x, size.y, size.z) * 0.5;
    result.root.position.sub(center);
    presentation.add(result.root);
    currentRoot = result.root;
    frameObject(presentation);
    if (!worldInitialized) {
      updateWorld = createWorldResponse(presentation, WORLD_VECTOR).update;
      const environment = createObservationEnvironment(scene, WORLD_VECTOR, radius);
      updateEnvironment = environment.update;
      const environmentController = createEnvironmentController(scene, lighting, environment);
      updateAppearance = environmentController.update;
      connectEnvironmentPanel(environmentController.setPreset);
      worldInitialized = true;
    }
    navigation.update(item, index);
    if (historyMode !== "none") updateCatalogUrl(item, historyMode === "replace");
    window.dispatchEvent(new CustomEvent("fractalar:itemchange", { detail: item.tokenNumber }));
    status.textContent = [
      titleCase(result.traits.rarity),
      result.traits.formFamily,
      result.traits.finishFamily,
      result.traits.patternFamily,
      result.traits.paletteFamily,
    ].join(" · ");
  } catch (error) {
    console.error(error);
    status.textContent = `${item.name} loaded, but its observation world could not be initialized.`;
    status.dataset.state = "error";
  } finally {
    navigation.setLoading(false);
  }
}

const resizeObserver = new ResizeObserver(resize);
resizeObserver.observe(canvas.parentElement ?? canvas);
resize();

const clock = new THREE.Clock();
renderer.setAnimationLoop(() => {
  const deltaSeconds = Math.min(clock.getDelta(), 0.05);
  updateWorld(deltaSeconds);
  updateEnvironment(deltaSeconds);
  updateAppearance(deltaSeconds);
  controls.update();
  renderer.render(scene, camera);
});

function renderKowry(collectionId: string, itemSeed: string) {
  return buildKowry({ collectionId, itemSeed });
}

function requireElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing required element: #${id}`);
  return element as T;
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

function disposeObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.geometry.dispose();
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    for (const material of materials) {
      for (const value of Object.values(material)) {
        if (value instanceof THREE.Texture) value.dispose();
      }
      material.dispose();
    }
  });
}
